// ============================================================
// useTurnTrackerAdmission - every way into the turn order.
//
// Part of useTurnTracker, split out by concern: the admission verbs
// (a tab, a batch, a custom entry, an import's whole new order) all go
// through one door, `admit`, and every arrival rolls initiative through
// one builder.
// ============================================================

import { useCallback } from 'react';
import { addParticipants as addParticipantsPure } from '../engine/turnTracker';
import { openFightOrder } from '../engine/phase/startFight';
import { isObject } from '../engine/bodyShape';
import type { Character } from '../engine/character/types';
import { rolledParticipant } from './turnTrackerRolls';
import type { AdmitEntryOptions, Commits, TrackerParams, TurnAdmission } from './useTurnTracker';
import type { StoredInitiative } from '../engine/roster/hidden';
import type { TurnParticipant, TurnTrackerState } from '../types';

interface Arrival {
  combatantId: string | null;
  name: string;
  isCustom?: boolean;
  sheet?: Character | null;
  initiative?: StoredInitiative | null;
}

/**
 * The arrivals a door may let in: never a hidden figure (ADR 2026-10-07), never an Object - a thing takes no turn
 * (2026-10-26). An arrival's own sheet is asked first, then the roster's.
 */
function admissible<T extends { combatantId?: string | null; sheet?: Character | null }>(
  entries: readonly T[],
  { isHidden, sheetFor }: Pick<TrackerParams, 'isHidden' | 'sheetFor'>,
): T[] {
  const thing = (e: T) => isObject(e.sheet ?? (e.combatantId ? sheetFor?.(e.combatantId) : null));
  const hidden = (e: T) => !!(isHidden && e.combatantId && isHidden(e.combatantId));
  return entries.filter((e) => !hidden(e) && !thing(e));
}

/** One participant, with its initiative already rolled if the rule says so. */
function useParticipantBuilder({ autoRollInitiative, sheetFor, initiativeModFor }: TrackerParams) {
  return useCallback(
    (arrival: Arrival): TurnParticipant =>
      rolledParticipant(arrival, { autoRoll: autoRollInitiative, sheetFor, initiativeModFor }),
    [autoRollInitiative, sheetFor, initiativeModFor],
  );
}

/**
 * THE DOOR. Every route into the turn order comes through here - a new
 * tab, a duplicate, the spawn dialog, a waiting room, the "+" button.
 * The arrival house rule decides whether an arrival acts this round.
 *
 * AUTO-ADMISSION IS NOT ITS OWN UNDO STEP. Creating a combatant admits
 * them to the order; that is one act, not two. Snapshotting here as well
 * put an extra entry on the stack holding the state from before the
 * combatant was even configured - so one Ctrl+Z after an attack landed
 * on THAT, and wounds came back at the factory default (v8.89.0). A
 * deliberate "Track Participant" (`undoable`) still is its own step.
 */
function useAdmit(p: TrackerParams, build: (arrival: Arrival) => TurnParticipant, { commitState }: Commits) {
  const { isLocked, isHidden, sheetFor, turnTrackerState, initiativeArrivals, initiativeTieBreak } = p;
  const { capturePreActionSnapshot, historyTrack } = p;
  return useCallback(
    (arrivals: readonly Arrival[], opts: { undoable?: boolean } = {}) => {
      const entries = admissible(arrivals, { isHidden, sheetFor });
      if (isLocked('turnTracker') || entries.length === 0) return;
      const built = entries.map((e) => build({ ...e, isCustom: e.isCustom ?? false }));
      const next = addParticipantsPure(turnTrackerState, built, {
        arrival: initiativeArrivals,
        tieBreak: initiativeTieBreak,
      });
      if (next === turnTrackerState) return; // every one of them a duplicate
      if (opts.undoable) {
        capturePreActionSnapshot();
        historyTrack.current = true;
      }
      commitState(next);
    },
    [
      turnTrackerState,
      isLocked,
      isHidden,
      sheetFor,
      build,
      initiativeArrivals,
      initiativeTieBreak,
      capturePreActionSnapshot,
      commitState,
      historyTrack,
    ],
  );
}

/**
 * VALIDATE AT THE BOUNDARY. The spawn dialog called the batch admission
 * with `{ tabId, name }` for five versions - every spawned combatant got a
 * `custom-` id and no link: no countdown tick, no ACTING NOW, and a name
 * that never followed a rename. The caller is .jsx, so tsc never saw it.
 * Skipped rather than admitted unlinked: a missing chip is obviously
 * wrong, an unlinked one is not.
 */
function linkedEntries(entries: TurnAdmission[] | null | undefined): TurnAdmission[] {
  return (entries ?? []).filter((e) => {
    if (e?.combatantId) return true;
    console.error('[turnTracker] batch entry has no combatantId - not adding it', e);
    return false;
  });
}

/** The admission verbs, all through the one door. */
function useAdmissionActions({ tabs }: TrackerParams, admit: ReturnType<typeof useAdmit>) {
  const addTabParticipant = useCallback(
    (combatantId: string, opts: AdmitEntryOptions = {}) => {
      const tab = tabs.find((t) => t.id === combatantId);
      if (!tab && !opts.name) return;
      const name = opts.name || tab?.target?.name || tab?.label || 'Unknown';
      admit([{ combatantId, name, sheet: opts.sheet, initiative: opts.initiative }], { undoable: opts.undoable });
    },
    [tabs, admit],
  );
  const addTabParticipantBatch = useCallback((entries: TurnAdmission[]) => admit(linkedEntries(entries)), [admit]);
  const addCustomParticipant = useCallback(
    (name: string) => admit([{ combatantId: null, name, isCustom: true }], { undoable: true }),
    [admit],
  );
  return { addTabParticipant, addTabParticipantBatch, addCustomParticipant };
}

/**
 * Rebuild the order from scratch - for IMPORT, which replaces the whole
 * roster: every participant the tracker held points at a combatant that
 * no longer exists. A fresh combat, round 1, everybody rolled. An empty
 * tracker is a combat being set up, so nobody is recorded as having
 * "joined" and no reroll hint appears - they all just rolled together.
 */
function useReplaceParticipants(p: TrackerParams, build: (arrival: Arrival) => TurnParticipant, commits: Commits) {
  const { isLocked, isHidden, sheetFor, turnTrackerState, initiativeTieBreak } = p;
  const { commitUndoable } = commits;
  return useCallback(
    (entries: TurnAdmission[]) => {
      if (isLocked('turnTracker')) return;
      const built = admissible(
        entries.filter((e) => e?.combatantId),
        { isHidden, sheetFor },
      ).map((e) => build({ ...e, isCustom: false }));
      const cleared: TurnTrackerState = { ...turnTrackerState, participants: [], activeIndex: 0, round: 1, joined: [] };
      commitUndoable(addParticipantsPure(cleared, built, { arrival: 'now', tieBreak: initiativeTieBreak }));
    },
    [turnTrackerState, isLocked, isHidden, sheetFor, build, initiativeTieBreak, commitUndoable],
  );
}

/**
 * OPEN A FIGHT (v8.430.0): everyone in it joins the order, round 1, the
 * surprised sitting out - one undoable step. Initiative is rolled here for
 * every entry; `openFightOrder` then keeps the standing numbers unless a
 * reroll was asked for.
 */
function useOpenFight(p: TrackerParams, build: (arrival: Arrival) => TurnParticipant, commits: Commits) {
  const { isLocked, isHidden, sheetFor, turnTrackerState, initiativeTieBreak } = p;
  const { commitUndoable } = commits;
  return useCallback(
    (entries: TurnAdmission[], opts: { reroll: boolean; sitsOut: readonly string[]; keep?: boolean }) => {
      if (isLocked('turnTracker')) return null;
      const fresh = admissible(linkedEntries(entries), { isHidden, sheetFor }).map((e) =>
        build({ ...e, isCustom: false }),
      );
      const opened = openFightOrder(turnTrackerState, fresh, { ...opts, tieBreak: initiativeTieBreak });
      commitUndoable(opened);
      // Handed back: the caller cannot read the order it just made until the next render.
      return opened;
    },
    [turnTrackerState, isLocked, isHidden, sheetFor, build, initiativeTieBreak, commitUndoable],
  );
}

/**
 * The admission verbs and the participant builder they share.
 *
 * `build` is returned for `participantFor` - a scene cut rolls its dice
 * through the same builder as everybody else.
 */
export function useAdmission(p: TrackerParams, commits: Commits) {
  const build = useParticipantBuilder(p);
  const admit = useAdmit(p, build, commits);
  return {
    build,
    ...useAdmissionActions(p, admit),
    replaceParticipants: useReplaceParticipants(p, build, commits),
    openFight: useOpenFight(p, build, commits),
  };
}
